// M3 – Danh sách đơn hàng POS từ hệ thống (API 1c, V2.3): đơn lịch hẹn + đơn khách lẻ; bấm đơn → Tra đơn / Thu nợ
import { useCallback, useEffect, useState } from 'react';
import { DatePicker, Input, Select, Table, Tabs } from 'antd';
import type { ColumnsType } from 'antd/es/table';
import dayjs, { type Dayjs } from 'dayjs';
import { useSearchParams } from 'react-router-dom';
import { Download, RefreshCw } from 'lucide-react';
import useTableColumnSettings from 'hooks/useTableColumnSettings';
import { ColumnSettingButton } from 'components/TableColumnSetting';
import { getPosOrders, type PosOrderList, type PosOrderListItem, type PosOrderTab } from 'api/PosV2';
import { METHOD_LABEL } from './data';
import { PAYMENT_STATUS_LABEL, methodFromApi, parseViDateTime, useServerBranches } from './remote';
import { StatusPill, exportExcel, fmtDateTime, fmtTime, formatPhone, money, usePosAuth } from './utils';
import PosShell from './components/PosShell';
import RemoteOrderModal from './components/RemoteOrderModal';

const TAB_LABEL: Record<PosOrderTab, string> = { all: 'Tất cả', completed: 'Hoàn thành', debt: 'Còn nợ', cancelled: 'Đã hủy' };

const PRESETS: { key: string; label: string; range: () => [Dayjs, Dayjs] }[] = [
  { key: 'today', label: 'Hôm nay', range: () => [dayjs(), dayjs()] },
  { key: '7d', label: '7 ngày', range: () => [dayjs().subtract(6, 'day'), dayjs()] },
  { key: 'month', label: 'Tháng này', range: () => [dayjs().startOf('month'), dayjs()] },
  { key: '30d', label: '30 ngày', range: () => [dayjs().subtract(29, 'day'), dayjs()] },
];

const PAY_PILL: Record<string, string> = {
  PAID: 'bg-green-50 text-green-700 ring-green-200',
  PARTIALLY_PAID: 'bg-amber-50 text-amber-700 ring-amber-200',
  UNPAID: 'bg-red-50 text-red-600 ring-red-200',
};
const TYPE_TAG: Record<string, string> = { INSTALLMENT: 'Trả góp', DEPOSIT: 'Đặt cọc' };
const SOURCE_LABEL: Record<string, string> = { appointment: 'Lịch hẹn', walk_in: 'Khách lẻ' };
const API_METHODS = ['CASH', 'BANK_TRANSFER', 'POS_CARD', 'EWALLET', 'PREPAID'];
const methodLabel = (m: string) => { const k = methodFromApi(m); return k ? METHOD_LABEL[k] : m; };
const createdIso = (o: PosOrderListItem) => parseViDateTime(o.created_at_iso || o.created_at);

export default function PosOrders() {
  const { can } = usePosAuth();
  const branches = useServerBranches();
  const [params, setParams] = useSearchParams();

  // Bộ lọc lưu trên URL để chia sẻ link
  const get = (k: string) => params.get(k) || '';
  const getList = (k: string) => (params.get(k) ? params.get(k)!.split(',') : []);
  const setParam = (patch: Record<string, string | string[] | null>, keepPage = false) => {
    const next = new URLSearchParams(params);
    Object.entries(patch).forEach(([k, v]) => {
      const val = Array.isArray(v) ? v.join(',') : v;
      if (val) next.set(k, val); else next.delete(k);
    });
    if (!keepPage) next.delete('page');
    setParams(next, { replace: true });
  };

  const from = get('from') ? dayjs(get('from')) : dayjs();
  const to = get('to') ? dayjs(get('to')) : from;
  const tab = (get('tab') || 'all') as PosOrderTab;
  const source = get('source');
  const payStates = getList('pay');
  const methods = getList('method');
  const branchIds = getList('branch');
  const page = Number(get('page')) || 1;
  const perPage = Number(get('per')) || 20;
  const search = get('q');
  const [q, setQ] = useState(search);
  useEffect(() => {
    const t = setTimeout(() => { if (q !== search) setParam({ q }); }, 400);
    return () => clearTimeout(t);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [q]);

  const [data, setData] = useState<PosOrderList | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [openOrder, setOpenOrder] = useState<number | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    const res = await getPosOrders({
      from: from.format('YYYY-MM-DD'), to: to.format('YYYY-MM-DD'), tab,
      ...(search ? { search } : {}),
      ...(source === 'appointment' || source === 'walk_in' ? { source } : {}),
      ...(payStates.length ? { payment_status: payStates.join(',') } : {}),
      ...(methods.length ? { method: methods.join(',') } : {}),
      ...(branchIds.length ? { branch_id: branchIds.join(',') } : {}),
      page, per_page: perPage,
    });
    setLoading(false);
    if (!res.ok || !res.data) { setError(res.message || 'Không tải được danh sách đơn'); return; }
    setError('');
    setData(res.data);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [params.toString()]);
  useEffect(() => { load(); }, [load]);

  const columns: ColumnsType<PosOrderListItem> = [
    {
      key: 'code', title: 'Mã đơn', fixed: 'left', width: 200,
      render: (_, o) => <span className="font-mono text-[13px] font-semibold text-[#2c6b7f]">{o.order_code}</span>,
    },
    {
      key: 'status', title: 'Thanh toán', width: 170,
      render: (_, o) => (
        <span className="flex flex-wrap items-center gap-1">
          <StatusPill label={PAYMENT_STATUS_LABEL[o.payment_status] || o.payment_status} cls={PAY_PILL[o.payment_status] || 'bg-slate-100 text-slate-600 ring-slate-200'} />
          {TYPE_TAG[o.payment_type] && <span className="rounded bg-slate-100 px-1.5 py-0.5 text-[11px] font-medium text-slate-600">{TYPE_TAG[o.payment_type]}</span>}
        </span>
      ),
    },
    { key: 'total', title: 'Tổng tiền', align: 'right', width: 130, render: (_, o) => <b>{money(o.final_amount)}</b> },
    { key: 'paid', title: 'Đã thu', align: 'right', width: 120, render: (_, o) => money(o.paid_amount) },
    { key: 'debt', title: 'Còn nợ', align: 'right', width: 120, render: (_, o) => (o.debt_amount > 0 ? <b className="text-amber-600">{money(o.debt_amount)}</b> : <span className="text-slate-400">0</span>) },
    {
      key: 'customer', title: 'Khách', width: 210,
      render: (_, o) => (
        <div className="leading-tight">
          <div className="font-medium">{o.customer?.name || 'Khách lẻ'}</div>
          {o.customer?.phone && <div className="text-xs text-slate-500">{formatPhone(String(o.customer.phone).replace(/\D/g, ''))}</div>}
        </div>
      ),
    },
    {
      key: 'time', title: 'Thời gian', width: 130,
      render: (_, o) => { const iso = createdIso(o); return iso ? (dayjs(iso).isSame(dayjs(), 'day') ? fmtTime(iso) : fmtDateTime(iso)) : '—'; },
    },
    {
      key: 'source', title: 'Nguồn', width: 110,
      render: (_, o) => (o.source === 'appointment'
        ? <span className="rounded border border-amber-200 bg-amber-50 px-1.5 py-0.5 text-xs font-medium text-amber-800">Lịch hẹn</span>
        : SOURCE_LABEL[o.source] || o.source),
    },
    { key: 'methods', title: 'PTTT', width: 170, render: (_, o) => (o.methods || []).map(methodLabel).join(', ') || '—' },
    { key: 'cashier', title: 'Thu ngân', width: 140, render: (_, o) => (o.cashiers || []).join(', ') || o.created_by_name || '—' },
    { key: 'items', title: 'Số dòng', align: 'right', width: 80, render: (_, o) => o.items_count },
    { key: 'branch', title: 'Chi nhánh', width: 160, render: (_, o) => o.branch?.name || '—' },
  ];
  const { columns: tableColumns, components, tableLayout, scrollX, settingProps } = useTableColumnSettings('pos_orders_v2', columns);

  const items = data?.items || [];
  const total = data?.pagination?.total || 0;
  const summary = data?.summary;

  const doExport = () => {
    exportExcel(`Don_hang_${from.format('YYYYMMDD')}_${to.format('YYYYMMDD')}_trang${page}.xlsx`, 'Đơn hàng', items.map(o => ({
      'Mã đơn': o.order_code, 'Thời gian': createdIso(o) ? fmtDateTime(createdIso(o)) : o.created_at, 'Chi nhánh': o.branch?.name || '',
      'Khách': o.customer?.name || 'Khách lẻ', 'SĐT': o.customer?.phone || '', 'Nguồn': SOURCE_LABEL[o.source] || o.source,
      'Tổng tiền': o.final_amount, 'Đã thu': o.paid_amount, 'Còn nợ': o.debt_amount,
      'Thanh toán': PAYMENT_STATUS_LABEL[o.payment_status] || o.payment_status, 'Hình thức': TYPE_TAG[o.payment_type] || 'Trả đủ',
      'PTTT': (o.methods || []).map(methodLabel).join(', '), 'Thu ngân': (o.cashiers || []).join(', '),
    })));
  };

  const preset = PRESETS.find(p => { const [a, b] = p.range(); return a.isSame(from, 'day') && b.isSame(to, 'day'); })?.key;

  return (
    <PosShell
      title="Đơn hàng"
      subtitle={loading ? 'Đang tải…' : `${total} đơn · Tổng ${money(summary?.final_amount || 0)}đ · Đã thu ${money(summary?.paid_amount || 0)}đ · Còn nợ ${money(summary?.debt_amount || 0)}đ`}
      actions={<>
        <button onClick={load} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50">
          <RefreshCw className={`h-4 w-4 ${loading ? 'animate-spin' : ''}`} /> Tải lại
        </button>
        {can('export') && <button onClick={doExport} disabled={!items.length} className="flex h-9 items-center gap-1.5 rounded-lg border border-slate-300 bg-white px-3 text-sm font-semibold text-slate-700 hover:bg-slate-50 disabled:opacity-50"><Download className="h-4 w-4" /> Xuất Excel</button>}
        <ColumnSettingButton {...settingProps} />
      </>}
    >
      <div className="mb-3 flex flex-wrap items-center gap-2">
        <div className="flex rounded-lg bg-slate-100 p-1">
          {PRESETS.map(p => (
            <button key={p.key} onClick={() => { const [a, b] = p.range(); setParam({ from: a.format('YYYY-MM-DD'), to: b.format('YYYY-MM-DD') }); }}
              className={`h-8 rounded-md px-3 text-sm font-medium ${preset === p.key ? 'bg-white text-[#2c6b7f] shadow-sm' : 'text-slate-600'}`}>{p.label}</button>
          ))}
        </div>
        <DatePicker.RangePicker value={[from, to]} format="DD/MM/YYYY" allowClear={false}
          onChange={v => v && v[0] && v[1] && setParam({ from: v[0].format('YYYY-MM-DD'), to: v[1].format('YYYY-MM-DD') })} />
        <Select mode="multiple" allowClear placeholder="Chi nhánh" className="min-w-[160px]" maxTagCount="responsive"
          value={branchIds} onChange={v => setParam({ branch: v })} options={branches.map(b => ({ value: String(b.id), label: b.name }))} />
        <Select allowClear placeholder="Nguồn" className="min-w-[120px]" value={source || undefined} onChange={v => setParam({ source: v || null })}
          options={[{ value: 'appointment', label: 'Lịch hẹn' }, { value: 'walk_in', label: 'Khách lẻ' }]} />
        <Select mode="multiple" allowClear placeholder="TT thanh toán" className="min-w-[150px]" maxTagCount="responsive"
          value={payStates} onChange={v => setParam({ pay: v })} options={['PAID', 'PARTIALLY_PAID', 'UNPAID'].map(k => ({ value: k, label: PAYMENT_STATUS_LABEL[k] }))} />
        <Select mode="multiple" allowClear placeholder="Phương thức" className="min-w-[140px]" maxTagCount="responsive"
          value={methods} onChange={v => setParam({ method: v })} options={API_METHODS.map(m => ({ value: m, label: methodLabel(m) }))} />
        <Input.Search allowClear placeholder="Mã đơn, SĐT, tên / mã khách…" value={q} onChange={e => setQ(e.target.value)} className="w-64" />
      </div>

      {error && (
        <div className="mb-3 flex items-center justify-between gap-2 rounded-lg border border-red-200 bg-red-50 px-3 py-2 text-sm text-red-700">
          <span>{error}</span>
          <button onClick={load} className="shrink-0 font-semibold underline">Thử lại</button>
        </div>
      )}

      <Tabs activeKey={tab} onChange={k => setParam({ tab: k === 'all' ? null : k })}
        items={(Object.keys(TAB_LABEL) as PosOrderTab[]).map(k => ({ key: k, label: `${TAB_LABEL[k]} (${data?.tab_counts?.[k] ?? 0})` }))} />

      <Table<PosOrderListItem>
        rowKey="order_id"
        size="middle"
        className="custom-ant-table"
        loading={loading}
        columns={tableColumns}
        components={components}
        tableLayout={tableLayout}
        scroll={{ x: scrollX }}
        dataSource={items}
        pagination={{
          current: page, pageSize: perPage, total, showSizeChanger: true, pageSizeOptions: [20, 50, 100], showTotal: t => `${t} đơn`,
          onChange: (p, size) => setParam({ page: p > 1 ? String(p) : null, per: size !== 20 ? String(size) : null }, true),
        }}
        onRow={o => ({ onClick: () => setOpenOrder(o.order_id), className: `cursor-pointer ${o.order_id === openOrder ? '!bg-[#ecf6f9]' : ''}` })}
        locale={{ emptyText: loading ? ' ' : 'Không có đơn phù hợp bộ lọc' }}
      />

      <RemoteOrderModal open={openOrder !== null} orderKey={openOrder} onClose={() => setOpenOrder(null)} onPaid={load} />
    </PosShell>
  );
}
